<template>
  <section class="w-full">
    <form
      @submit.prevent="
       Object.keys(props.addinfoToUpdate).length > 0 
       ? updateAdditionalInfo() 
       : addNewAdditionalInfo()
      "
      class="grid grid-cols-12 gap-4 px-4 mt-3 text-base font-normal"
    >
      <span class="col-span-12">
        <label class="required-sign">{{ translate("information", "ucfirst") }}</label>
        <CustomInputText name="value" class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('information type', 'ucfirst') }}</label>
        <CustomDropdown name="add_information_type_id" :options="informationTypes" optionLabel="add_information_type_name" optionValue="add_information_type_id" :loading="fetchingInfoType" 
        class="w-full px-3 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('information order', 'ucfirst') }}</label>
        <CustomDropdown name="add_information_order" :options="informationOrders" optionLabel="order" optionValue="order"  
        class="w-full px-3 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12"> 
        <Button
          v-if="Object.keys(props.addinfoToUpdate).length > 0"
          @click="showConfirmDialog = true"
          type="button"
          :label="translate('delete', 'ucfirst')"
          class="text-white px-5 py-2 float-left rounded-full bg-red-600"
        />
        <Button
          type="submit"
          :label="
            Object.keys(props.addinfoToUpdate).length > 0
              ? translate('update', 'ucfirst')
              : translate('save', 'ucfirst')
          "
          class="text-white px-5 py-2 float-right rounded-full bg-sea-serpent"
        />
      </span>
    </form>

    <CustomConfirmDialog
      :show-confirm="showConfirmDialog" @confirmResponse="confirmDialogResponse"
    />
    <LoadingDialog :visible="isLoading" />
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeMount } from "vue";
import CustomInputText from "@/components/Base/CustomInputText.vue";
import CustomDropdown from "@/components/Base/CustomDropdown.vue";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { showToast } from "@/composables/toast";
import { translate } from "@/composables/translate";
import requestHandler from "@/composables/requestHandler";
import LoadingDialog from "@/components/Utils/LoadingDialog.vue";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { object, string, number } from "yup";
import CustomConfirmDialog from "@/components/Utils/CustomConfirmDialog.vue";

let toast = useToast();
let isLoading = ref(false);
let fetchingInfoType = ref(false)
let informationTypes = ref([])
let informationOrders = ref([
  {order:1},{order:2},{order:3},{order:4},{order:5},{order:6},{order:7},
  {order:8},{order:9},{order:10},{order:11},{order:12},{order:13},{order:14},
  {order:15}
])
let showConfirmDialog = ref(false);

// Tipos de informação que requerem um link
let addInfoLinks = [
  {id: 1, name: 'portfolio'},
  {id: 2, name: 'github'},
  {id: 3, name: 'linkedin'},
  {id: 4, name: 'website'},
  {id: 5, name: 'blog'}
]


const props = defineProps({
  addinfoToUpdate: {
    type: Object,
    required: false,
    default: () => {}
  },
  curriculumId: {
    type: Number,
    required: false
  },
});

const emit = defineEmits(["closeModal",'addinformationUpdated']);

// ############ Validação formulário ############ 2

const { handleSubmit, setValues,  setFieldError } = useForm({
  validationSchema: toTypedSchema(
    object().shape({
      value: string()
        .required(translate("this field is required", "ucfirst"))
        .min(2, translate("this field must be at least 2 characters", "ucfirst"))
        .max(255, translate("this field must be at most 255 characters", "ucfirst"))
        .ensure().trim().default(""),
      add_information_type_id: number().required(translate('this field is required', 'ucfirst')),
      add_information_order: number().required(translate('this field is required', 'ucfirst'))
    })
  ),
});

// ############ Criação de Métodos/Funções ############

const addNewAdditionalInfo = handleSubmit(async (values) => {
  
  if (!validateLink(values)) return

 const newValues = {
  curriculum_id: props.curriculumId,
  value: values.value,
  add_information_type_id: values.add_information_type_id,
  add_information_order: values.add_information_order
 }

  if (values) {
    isLoading.value = !isLoading.value;
    const response = await requestHandler.executeRequest("/api/curriculum/additional-information/create", newValues ,"post");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(toast,
        translate("there was an error adding your additional information", "ucfirst"),"error");
      return;
    }

    showToast(toast, translate("added additional information", "ucfirst"));
    emit("addinformationUpdated", true);
    emit("closeModal", false);
  }
});

let updateAdditionalInfo = handleSubmit(async (values) => {

  if (!validateLink(values)) return
  
  const newValues = {
  value: values.value,
  add_information_type_id: values.add_information_type_id,
  add_information_order: values.add_information_order
 }

  if (values) {
    isLoading.value = !isLoading.value;
    const response = await requestHandler.executeRequest(`/api/curriculum/additional-information/update/${props.addinfoToUpdate.id}`, newValues ,"put");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(
        toast,
        translate("there was an error updating your additional information", "ucfirst"),
        "warn"
      );
      return;
    }
    showToast(toast, translate("your additional information was updated", "ucfirst"));
    emit("addinformationUpdated", true);
    emit("closeModal", false);
  }
});

let deleteInformation = handleSubmit(async (values) => {

  const deleteBody = {
    item_type: "additional",
    item_id: props.addinfoToUpdate.id,
    curriculum_id: props.curriculumId
  };

  if (values) {
    isLoading.value = !isLoading.value;
    const response = await requestHandler.executeRequest("/api/curriculum/item/delete", deleteBody,"delete");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(toast,translate("there was an error deleting your additional information", "ucfirst"),"warn");
      return;
    }
    showToast(toast, translate("your additional information was deleted", "ucfirst"));
    emit("closeModal", false);
    emit("addinformationUpdated", true);
  }
});

function confirmDialogResponse(event) {
  
  showConfirmDialog.value = event.close;
  if (event.accepted) {
    deleteInformation()
  } 
}

async function getListInformationTtypes() {

  fetchingInfoType.value = !fetchingInfoType.value
  const response = await requestHandler.executeRequest("/api/curriculum/list-addinformation-types", {} ,"get");
 
  if (response.success) {
    informationTypes.value = response.data
  } 

  fetchingInfoType.value = !fetchingInfoType.value
}

function isValidUrl(string) {
  try {
    new URL(string);
    return true;
  } catch (err) {
    return false;
  }
}

function validateLink(valuesForm) {
  let valid = true
  addInfoLinks.forEach(type => {
    if (type.id == valuesForm.add_information_type_id) {
      if (!isValidUrl(valuesForm.value)) {
        setFieldError('value', 'please, type a valid URL')
        valid = false;
      }
    }
  });
  return valid
}

onBeforeMount(() => {
  getListInformationTtypes()
});

onMounted(() => {
  if (Object.keys(props.addinfoToUpdate).length > 0) {
    setValues({
      value: props.addinfoToUpdate.value,
      add_information_type_id: props.addinfoToUpdate.type,
      add_information_order: props.addinfoToUpdate.order
    });
  }
})
</script>
